import Account from "@/src/components/settings/account/account";
import Appearance from "@/src/components/settings/appearance/appearance";
import General from "@/src/components/settings/general/general";
import ScTabs from "@/src/components/ui-kit/tabs";
import { COLOR_MODES, PRESET_THEMES } from "@/src/constants/appearance";
import { useTheme } from "@/src/styles/theme-provider";
import { derivePalette } from "@/src/utils/derive-palette";
import { Setting3 } from "iconsax-react-nativejs";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";

const TAB_KEYS = {
  GENERAL: "LABEL_GENERAL",
  ACCOUNT: "LABEL_ACCOUNT",
  APPEARANCE: "LABEL_APPEARANCE",
} as const;

const tabs = [
  TAB_KEYS.GENERAL,
  TAB_KEYS.ACCOUNT,
  TAB_KEYS.APPEARANCE,
];

type SearchResult = Readonly<{
  key: string;
  label: string;
  hint: string;
  leading: React.ReactNode;
  onPress: () => void;
}>;

const SettingScreen = () => {
  const { t } = useTranslation();
  const { themeVars, setTheme, setPrimaryPalette, setSavedThemeId } =
    useTheme();

  const [active, setActive] = useState<string>(TAB_KEYS.GENERAL);
  const [searchQuery, setSearchQuery] = useState("");

  const slate = themeVars["--color-slate-default"] as string;

  // Everything reachable from settings, flattened so it can be searched and
  // jumped to directly (tabs, color modes, and every preset theme).
  const searchIndex = useMemo<SearchResult[]>(() => {
    const tabResults: SearchResult[] = tabs.map((tab) => ({
      key: `tab-${tab}`,
      label: t(tab),
      hint: t("BTN_SETTINGS"),
      leading: <Setting3 size={18} color={slate} />,
      onPress: () => setActive(tab),
    }));

    const modeResults: SearchResult[] = COLOR_MODES.map((mode) => ({
      key: `mode-${mode.id}`,
      label: t(mode.labelKey),
      hint: t("LABEL_APPEARANCE"),
      leading: <mode.icon size={18} color={slate} />,
      onPress: () => {
        setActive(TAB_KEYS.APPEARANCE);
        setTheme(mode.id);
      },
    }));

    const presetResults: SearchResult[] = PRESET_THEMES.map((preset) => ({
      key: `preset-${preset.id}`,
      label: preset.name,
      hint: t("LABEL_PRESET_THEMES"),
      leading: (
        <View
          className="w-5 h-5 rounded-full border border-ash-3"
          style={{ backgroundColor: preset.color }}
        />
      ),
      onPress: () => {
        setActive(TAB_KEYS.APPEARANCE);
        setPrimaryPalette(derivePalette(preset.color));
        setSavedThemeId(preset.id);
      },
    }));

    return [...tabResults, ...modeResults, ...presetResults];
  }, [t, slate, setTheme, setPrimaryPalette, setSavedThemeId]);

  const query = searchQuery.trim().toLowerCase();
  const results = query
    ? searchIndex.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.hint.toLowerCase().includes(query),
    )
    : [];

  const handleResultPress = (item: SearchResult) => {
    item.onPress();
    setSearchQuery("");
  };

  return (
    <View className="bg-obsidian flex-1 gap-4">
      <ScrollView
        contentContainerClassName="gap-4"
        showsVerticalScrollIndicator={false}
      >
            <View className="flex-row rounded-2xl border-ash-3 justify-between border bg-chalk p-6">
              <ScTabs tabs={tabs} active={active} onChange={setActive} />
            </View>

            {/* Content card */}
            <View className="flex-1 bg-chalk rounded-2xl md:rounded-[16px] p-4 md:border md:border-ash-3">
              {active === TAB_KEYS.GENERAL && <General />}
              {active === TAB_KEYS.ACCOUNT && <Account />}
              {active === TAB_KEYS.APPEARANCE && <Appearance />}
            </View>
      </ScrollView>
    </View>
  );
};

export default SettingScreen;
